import React, { Suspense } from 'react';
import MarketingImage from './MarketingImage';
import { isAppPath, navigateToPath } from '../utils/navigation';

const CompanyLogos = React.lazy(() => import('./CompanyLogos').then((module) => ({
  default: module.CompanyLogos,
})));

const FOOTER_LINK_SECTIONS = [
  {
    title: 'Platform',
    links: [
      { label: 'Features', href: '/features/' },
      { label: 'Pricing', href: '/pricing/' },
      { label: 'Question Bank', href: '/question-bank/' },
      { label: 'Resume Analyzer', href: '/resume-analyzer/' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Help Center', href: '/help/' },
      { label: 'FAQ', href: '/faq/' },
      { label: 'Contact Us', href: '/contact/' },
      { label: 'Cookie Policy', href: '/cookies/' },
      { label: 'Sitemap', href: '/sitemap/' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About TryInterview', href: '/about/' },
      { label: 'Privacy Policy', href: '/privacy/' },
      { label: 'Terms of Service', href: '/terms/' },
    ],
  },
  {
    title: 'Popular Guides',
    links: [
      { label: 'AI Mock Interview Practice', href: '/ai-mock-interview-practice/' },
      { label: 'Free Mock Interview Online', href: '/free-mock-interview-online/' },
      { label: 'Interview Practice for Freshers', href: '/interview-practice-for-freshers/' },
      { label: 'Software Engineer Mock Interview AI', href: '/software-engineer-mock-interview-ai/' },
      { label: 'Get Interview Feedback Instantly', href: '/get-interview-feedback-instantly/' },
    ],
  },
];

function LandingPageBelowFold({ onGetStarted }) {
  const handleInternalNavigation = (event, href) => {
    if (!isAppPath(href)) {
      return;
    }

    event.preventDefault();
    navigateToPath(href);
  };

  return (
    <>
      <section className="content-section" id="problem">
        <div className="section-wrapper">
          <div className="text-container">
            <h2 className="section-title">Feeling Confused About Interview Prep?</h2>
            <p className="section-description">
              Finding the perfect mock interview platform shouldn&apos;t be complicated. Many candidates struggle
              with where to start, what to practice, and how to improve effectively.
            </p>
            <ul className="feature-list">
              <li>❌ Generic interview questions that don&apos;t match your industry</li>
              <li>❌ No personalized feedback on your responses</li>
              <li>❌ Lack of real-time practice opportunities</li>
              <li>❌ Expensive coaching that breaks the bank</li>
            </ul>
          </div>

          <div className="image-container">
            <MarketingImage
              src="/confuse-to-get-perfect-mock-interview-platform-.png"
              alt="Illustration of a candidate navigating interview preparation options"
              className="section-image"
              sizes="(max-width: 1024px) 88vw, 460px"
            />
          </div>
        </div>
      </section>

      <section className="content-section" id="pain-point">
        <div className="section-wrapper reverse">
          <div className="image-container">
            <MarketingImage
              src="/failed-Interview.png"
              alt="Illustration representing the frustration of repeated interview rejection"
              className="section-image"
              sizes="(max-width: 1024px) 88vw, 460px"
            />
          </div>

          <div className="text-container">
            <h2 className="section-title">Don&apos;t Let Failed Interviews Define You</h2>
            <p className="section-description">
              We understand the frustration of interview rejections. Every &quot;no&quot; feels personal,
              but it&apos;s often just a matter of better preparation and practice.
            </p>
            <ul className="feature-list">
              <li>💔 Interview anxiety affecting your performance</li>
              <li>💔 Struggling to articulate your experience</li>
              <li>💔 Missing opportunities due to lack of preparation</li>
              <li>💔 Feeling unprepared for technical or behavioral questions</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="content-section" id="solution">
        <div className="section-wrapper">
          <div className="text-container">
            <h2 className="section-title">Build Unshakeable Interview Confidence</h2>
            <p className="section-description">
              TryInterview transforms nervous candidates into confident professionals.
              Our AI-powered platform provides the practice and feedback you need to excel.
            </p>
            <ul className="feature-list">
              <li>✅ Industry-specific interview simulations</li>
              <li>✅ Real-time AI feedback on your answers</li>
              <li>✅ Personalized improvement recommendations</li>
              <li>✅ Track your progress with detailed analytics</li>
            </ul>
          </div>

          <div className="image-container">
            <MarketingImage
              src="/confidence-with-interview.png"
              alt="Illustration of a confident interview conversation powered by practice"
              className="section-image"
              sizes="(max-width: 1024px) 88vw, 460px"
            />
          </div>
        </div>
      </section>

      <section className="content-section" id="practice">
        <div className="section-wrapper reverse">
          <div className="image-container">
            <MarketingImage
              src="/practice-interview-online.png"
              alt="TryInterview illustration showing interview practice available online"
              className="section-image"
              sizes="(max-width: 1024px) 88vw, 460px"
            />
          </div>

          <div className="text-container">
            <h2 className="section-title">Practice Anytime, Anywhere</h2>
            <p className="section-description">
              No scheduling headaches. No time zone conflicts. Practice interviews 24/7
              at your own pace with our AI-powered platform.
            </p>
            <ul className="feature-list">
              <li>🎯 Unlimited practice sessions</li>
              <li>🎯 Question bank with 1,080 curated questions</li>
              <li>🎯 Resume analysis and optimization</li>
              <li>🎯 Detailed performance analytics and progress tracking</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="content-section" id="questions">
        <div className="section-wrapper">
          <div className="text-container">
            <h2 className="section-title">Master Common Interview Questions</h2>
            <p className="section-description">
              Access our comprehensive database of interview questions used by top companies.
              Practice with real questions from Google, Amazon, Microsoft, and more.
            </p>
            <ul className="feature-list">
              <li>📚 Technical interview questions</li>
              <li>📚 Behavioral interview scenarios</li>
              <li>📚 Case study questions</li>
              <li>📚 System design challenges</li>
            </ul>
          </div>

          <div className="image-container">
            <MarketingImage
              src="/common-interview-questions.png"
              alt="Photo representing practice with common interview questions"
              className="section-image section-image--wide"
              sizes="(max-width: 1024px) 88vw, 520px"
            />
          </div>
        </div>
      </section>

      <section className="content-section" id="success">
        <div className="section-wrapper reverse">
          <div className="image-container">
            <MarketingImage
              src="/sit-for-interview-after-using-tryinterview.png"
              alt="Illustration of a candidate feeling ready for an interview after practice"
              className="section-image"
              sizes="(max-width: 1024px) 88vw, 460px"
            />
          </div>

          <div className="text-container">
            <h2 className="section-title">Walk Into Interviews With Confidence</h2>
            <p className="section-description">
              After using TryInterview, you&apos;ll sit for interviews feeling prepared, confident,
              and ready to showcase your best self. Join thousands who&apos;ve transformed their careers.
            </p>
            <ul className="feature-list">
              <li>🌟 98% of users report increased confidence</li>
              <li>🌟 Average 3x improvement in interview performance</li>
              <li>🌟 50,000+ successful job placements</li>
              <li>🌟 Trusted by candidates at Fortune 500 companies</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="content-section" id="results">
        <div className="section-wrapper">
          <div className="text-container">
            <h2 className="section-title">Join 50,000+ Successful Candidates</h2>
            <p className="section-description">
              Don&apos;t just take our word for it. See the results our users achieve when they
              commit to mastering their interview skills with TryInterview.
            </p>
            <ul className="feature-list">
              <li>🎊 Land your dream job faster</li>
              <li>🎊 Get higher salary offers</li>
              <li>🎊 Stand out from other candidates</li>
              <li>🎊 Build lifelong interview skills</li>
            </ul>
          </div>

          <div className="image-container">
            <MarketingImage
              src="/successed-interview-with-tryinterview.site.png"
              alt="Illustration celebrating interview success after using TryInterview"
              className="section-image"
              sizes="(max-width: 1024px) 88vw, 460px"
            />
          </div>
        </div>
      </section>

      <section className="final-cta">
        <div className="cta-container">
          <h2 className="cta-title">Ready to Ace Your Next Interview?</h2>
          <p className="cta-description">
            Join TryInterview today and start practicing with AI-powered mock interviews.
            Your dream job is just one successful interview away.
          </p>
          <button type="button" className="cta-btn" onClick={onGetStarted}>
            Start Your Free Trial
          </button>
          <p className="cta-note">No credit card required • One free trial • Cancel anytime</p>
        </div>
      </section>

      <section className="trusted-companies" aria-labelledby="trusted-companies-title">
        <div className="trusted-container">
          <h2 id="trusted-companies-title" className="trusted-subtitle">
            Trusted by professionals at leading companies worldwide
          </h2>

          <div className="logos-scroll-wrapper">
           <div className="logos-scroll" aria-hidden="true">
  <Suspense fallback={null}>
    <CompanyLogos />
    <CompanyLogos />
  </Suspense>
</div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand-section">
            <div className="footer-logo">
              <MarketingImage
                src="/logo-icon.svg"
                alt="TryInterview"
                className="footer-logo-img"
              />
              <span className="footer-brand-name">TryInterview</span>
            </div>
            <p className="footer-tagline">AI-Powered Interview Preparation Platform</p>
            <p className="footer-description">
              Master your interview skills with our intelligent mock interviews,
              instant feedback, and comprehensive analytics. Join thousands of successful candidates.
            </p>
            <div className="footer-actions">
              <button type="button" className="footer-primary-action" onClick={onGetStarted}>
                Start Free Trial
              </button>
              <a
                href="/pricing/"
                className="footer-secondary-action"
                onClick={(event) => handleInternalNavigation(event, '/pricing/')}
              >
                View Pricing
              </a>
            </div>
            
          </div>

          <h2 className="sr-only">Footer navigation</h2>
          <div className="footer-links-grid">
            {FOOTER_LINK_SECTIONS.map((section) => (
              <div className="footer-section" key={section.title}>
                <h3>{section.title}</h3>
                <ul>
                  {section.links.map((link) => (
                    <li key={link.href}>
                      <a
                        href={link.href}
                        onClick={(event) => handleInternalNavigation(event, link.href)}
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
       <div className="footer-social">
  {/* Twitter / X */}
  <a
    href="https://twitter.com/TryinterviewAI"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Twitter"
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
    </svg>
  </a>

  {/* LinkedIn */}
  <a
    href="https://linkedin.com/company/tryinterview"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="LinkedIn"
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  </a>

  {/* GitHub */}
  <a
    href="https://github.com/beeyoow"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="GitHub"
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
    </svg>
  </a>

  {/* YouTube */}
  <a
    href="https://youtube.com/@TryinterviewAI"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="YouTube"
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 00.5 6.2 31.2 31.2 0 000 12a31.2 31.2 0 00.5 5.8 3 3 0 002.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 002.1-2.1A31.2 31.2 0 0024 12a31.2 31.2 0 00-.5-5.8zM9.8 15.5v-7l6.2 3.5-6.2 3.5z" />
    </svg>
  </a>

  {/* Instagram */}
  <a
    href="https://instagram.com/tryinterviewai"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Instagram"
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M7 2C4.2 2 2 4.2 2 7v10c0 2.8 2.2 5 5 5h10c2.8 0 5-2.2 5-5V7c0-2.8-2.2-5-5-5H7zm10 2a1 1 0 110 2 1 1 0 010-2zm-5 2.5A5.5 5.5 0 1112 17.5 5.5 5.5 0 0112 6.5zm0 2A3.5 3.5 0 1012 15.5 3.5 3.5 0 0012 8.5z" />
    </svg>
  </a>

  {/* TikTok */}
  <a
    href="https://tiktok.com/@tryinterviewai001"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="TikTok"
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 8.2a6.5 6.5 0 01-4-1.4v6.8a5.6 5.6 0 11-5.6-5.6c.3 0 .7 0 1 .1v2.8a2.8 2.8 0 102.8 2.8V2h2.8a4 4 0 003.9 3.4v2.8z" />
    </svg>
  </a>
</div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2026 TryInterview by Beeynow. All rights reserved.</p>
          <div className="footer-bottom-links">
            <a href="/privacy/" onClick={(event) => handleInternalNavigation(event, '/privacy/')}>
              Privacy
            </a>
            <span>•</span>
            <a href="/terms/" onClick={(event) => handleInternalNavigation(event, '/terms/')}>
              Terms
            </a>
            <span>•</span>
            <a href="/cookies/" onClick={(event) => handleInternalNavigation(event, '/cookies/')}>
              Cookies
            </a>
          </div>
        </div>
      </footer>
    </>
  );
}

export default LandingPageBelowFold;
